import { Link, Navigate } from "react-router-dom";
import { ArrowRight, BookOpen, Compass, LogOut, ShieldCheck } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import StarfieldCanvas from "../components/StarfieldCanvas";
import Reveal from "../components/Reveal";
import MaskedLines from "../components/MaskedLines";
import { useAuth } from "../context/AuthContext";

export default function Account() {
  usePageMeta(
    "Your Account — Consciousness Ascending",
    "Your member access for Your Own Remote Path."
  );
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin-slow rounded-full border border-[#8B5CF6]/30 border-t-[#8B5CF6]" />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  return (
    <section className="relative overflow-hidden" data-testid="account-page">
      <StarfieldCanvas className="absolute inset-0 h-full w-full" interactive={false} />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.14),transparent_55%)]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 sm:pt-40 lg:px-10 lg:pt-44">
        <p className="eyebrow">Member Access</p>
        <h1 className="font-display mt-6 font-light leading-[1.05] tracking-tight text-[clamp(2.1rem,6vw,4rem)]">
          <MaskedLines lines={["Welcome back,", user.name?.split(" ")[0] || "traveler."]} />
        </h1>

        <Reveal delay={0.4} className="mt-14">
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="flex flex-col gap-6 border border-white/[0.08] bg-[#0F121B]/70 p-8 sm:flex-row sm:items-center sm:p-10">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || "Member"}
                    className="h-20 w-20 rounded-full border border-[#8B5CF6]/50 object-cover shadow-[0_0_28px_rgba(139,92,246,0.3)]"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[#8B5CF6]/50 bg-[#8B5CF6]/10 font-display text-3xl text-[#A78BFA]">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p data-testid="account-greeting" className="font-display text-2xl text-[#F0F2F8]">
                    {user.name || "Practitioner"}
                  </p>
                  <p className="mt-1 text-sm text-[#8E95A5]">{user.email}</p>
                  <p className="mt-4 inline-flex items-center gap-2 border border-[#38BDF8]/30 bg-[#38BDF8]/10 px-3 py-1.5 text-[10px] font-mono-ui uppercase tracking-[0.2em] text-[#7DD3FC]">
                    <ShieldCheck className="h-3.5 w-3.5" /> Secured via Google · 7-day session
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                <Link
                  to="/guidebooks"
                  data-testid="account-guidebooks-btn"
                  className="inline-flex items-center gap-2 border border-[#8B5CF6] bg-[#8B5CF6]/15 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:bg-[#8B5CF6]/30"
                >
                  <BookOpen className="h-4 w-4 text-[#A78BFA]" /> Continue your path
                </Link>
                <Link
                  to="/remote-viewing"
                  data-testid="account-rv-btn"
                  className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:border-[#38BDF8]/70"
                >
                  <Compass className="h-4 w-4 text-[#7DD3FC]" /> The methodologies
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 lg:col-start-9">
              <div className="border border-white/[0.08] bg-[#0F121B]/70 p-8">
                <p className="eyebrow">Session</p>
                <p className="body-muted mt-4 text-sm leading-relaxed">
                  You are signed in with your Google account. Your session is
                  protected by a signed, HTTP-only cookie for seven days, then
                  expires quietly. No passwords, no noise — your path stays yours.
                </p>
                <button
                  data-testid="account-signout-btn"
                  onClick={logout}
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:border-red-400/60 hover:text-red-300"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
